import {useEffect, useRef, type ReactNode} from "react";

import {cn} from "@/lib/utils";
import {COMPLIANCE_STICKY_BAR_DATA_ATTR} from "@/src/modules/core/lib/compliance-sticky-bar-layout";
import {clampSheetFormScrollBody} from "@/src/modules/help/lib/help-tour-scroll";

type SheetFormScrollBodyProps = {
  children: ReactNode;
  className?: string;
};

function scheduleSheetFormScrollClamp(container: HTMLElement) {
  requestAnimationFrame(() => {
    clampSheetFormScrollBody(container);
    requestAnimationFrame(() => {
      clampSheetFormScrollBody(container);
    });
  });
}

/**
 * Inner scroll region for help-enabled create/edit sheets.
 * SheetContent should use `flex flex-col overflow-hidden`; header stays fixed, body scrolls.
 * Tour auto-scroll (`help-tour-scroll.ts`) targets this scroller inside `[data-slot='sheet-content']`.
 */
export function SheetFormScrollBody({children, className}: SheetFormScrollBodyProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const onFocusIn = () => {
      scheduleSheetFormScrollClamp(el);
    };

    const onScroll = () => {
      clampSheetFormScrollBody(el);
    };

    el.addEventListener("focusin", onFocusIn);
    el.addEventListener("scroll", onScroll, {passive: true});

    const onComplianceBarChange = () => {
      scheduleSheetFormScrollClamp(el);
    };

    const complianceObserver = new MutationObserver(onComplianceBarChange);
    complianceObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [COMPLIANCE_STICKY_BAR_DATA_ATTR],
    });

    const onViewportResize = () => {
      if (document.documentElement.hasAttribute(COMPLIANCE_STICKY_BAR_DATA_ATTR)) {
        scheduleSheetFormScrollClamp(el);
      }
    };

    window.visualViewport?.addEventListener("resize", onViewportResize);

    return () => {
      el.removeEventListener("focusin", onFocusIn);
      el.removeEventListener("scroll", onScroll);
      complianceObserver.disconnect();
      window.visualViewport?.removeEventListener("resize", onViewportResize);
    };
  }, []);

  return (
    <div
      ref={scrollRef}
      data-sheet-form-scroll-body
      className={cn("min-h-0 flex-1 overflow-y-auto", className)}
    >
      {children}
    </div>
  );
}
